import { request } from 'src/shared/utils/request';

/**
 * Extracts the filename from the 'Content-Disposition' header.
 *
 * @param headers - The HTTP headers object containing the 'Content-Disposition' header.
 * @returns The filename if present in the 'Content-Disposition' header; otherwise, undefined.
 */
export const getFilenameFromHeaders = (headers: Headers) =>
  headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1];

/**
 * Downloads a file from the specified URL.
 *
 * @param params.url - The URL of the file to download.
 * @param params.name - The name to save the file as. If not provided, the file name will be extracted from the URL.
 * @param params.prefetch - Prefetch the file (allows the browser to change the filename).
 */
export const downloadFromUrl = async ({
  url,
  name,
  prefetch = false,
}: {
  /** The URL of the file to download. */
  url: string;
  /** The name to save the file as. If not provided, the file name will be extracted from the URL. */
  name?: string;
  /** Prefetch the file (allows the browser to change the filename). */
  prefetch?: boolean;
}) => {
  if (!url) return null;

  let fileUrl = url;
  let fileName: string | undefined;

  if (prefetch) {
    const response = await request(url, {
      mode: 'cors', // Ensure CORS is enabled if cross-origin
    });
    fileName = getFilenameFromHeaders(response.headers);

    const blob = await response.blob();

    fileUrl = URL.createObjectURL(blob);
  }

  const a = document.createElement('a');
  a.href = fileUrl;
  a.download = name ?? fileName ?? url.split('/').pop() ?? 'download';

  a.click();
  a.remove();
  if (prefetch) URL.revokeObjectURL(fileUrl);
  return fileUrl;
};
